import React from "react";
import { H2, InternalLink, P, Strong, UL } from "../components/Prose";

// Figures are from the pl-gami-matches table as of 23 September 2026.
export default function HomeAwayScorigami() {
  return (
    <>
      <H2>The classic count has stalled</H2>
      <P>
        The classic scorigami treats 3–1 and 1–3 as the same result. By that
        measure the Premier League has produced 36 scorelines, and the last new
        one was West Brom 5–5 Manchester United on 19 May 2013. In the 4,990
        matches since, every final score has been one the league had already
        seen.
      </P>

      <H2>Home advantage shapes the grid</H2>
      <P>
        Split every result by venue and those 36 scorelines become{" "}
        <Strong>60</Strong>: 30 kinds of home win, 24 kinds of away win and 6
        draws. Home teams have won 45.6% of Premier League matches and away
        teams 28.7%, so the away side of the grid fills far more slowly. Home
        sides average 1.53 goals a game to the away side's 1.16, and they have
        won by five goals or more 197 times, against 70 for away teams. That
        leaves more empty squares within reach on the away side, and it is why
        new results keep appearing there.
      </P>

      <H2>The season without crowds</H2>
      <P>
        One season breaks the pattern. 2020/21, played almost entirely behind
        closed doors, is the only Premier League season in which away teams
        won more matches than home teams: 153 away wins to 144 home wins, with
        away sides taking 40.3% of results against a long-run 28.7%. One of the
        new scorigamis, Crystal Palace 0–7 Liverpool, came that December. With
        home advantage back, the away side of the grid has gone back to filling
        slowly.
      </P>

      <H2>The new scorigamis</H2>
      <P>Since the classic count stopped moving, four home/away results have happened for the first time:</P>
      <UL>
        <li>
          <Strong>Cardiff City 3–6 Liverpool</Strong>, 22 March 2014: the first
          away 6–3.
        </li>
        <li>
          <Strong>Southampton 0–9 Leicester City</Strong>, 25 October 2019: the
          biggest away win in Premier League history.
        </li>
        <li>
          <Strong>Crystal Palace 0–7 Liverpool</Strong>, 19 December 2020.
        </li>
        <li>
          <Strong>Sheffield United 0–8 Newcastle</Strong>, 24 September 2023:
          the most recent scorigami of any kind.
        </li>
      </UL>
      <P>Liverpool, fittingly, won away in two of them.</P>

      <H2>What could come next</H2>
      <P>
        The best candidates are away results whose home mirror image has
        already happened. An <Strong>away 7–2 win</Strong> is the likeliest: it
        has never happened, while 7–2 has been a home result five times
        (Blackburn v Sheffield Wednesday in 1997, Tottenham v Southampton in
        2000, Chelsea v Sunderland in 2010, Manchester City v Stoke in 2017 and
        Aston Villa v Liverpool in 2020). Five more away scorelines have never
        happened although their home versions each have once: 4–7, 4–6, 1–9,
        2–8 and 3–7.
      </P>
      <P>
        Draws are the other frontier. Only one draw has produced more than
        eight goals (5–5, once), and 4–4 has happened 18 times, so a 6–6 would
        be a genuine first.
      </P>
      <P>
        When one does happen, <Strong>@pl_gami</Strong> posts a home/away
        scorigami alert within minutes of full time, and the square lights up on
        the <InternalLink to="/">grid</InternalLink>. For how the grid works,
        start with{" "}
        <InternalLink to="/articles/what-is-scorigami">what a scorigami is</InternalLink>.
      </P>
    </>
  );
}
